import React, { useEffect, useMemo, useState } from "react";
import { ForgeNavBar } from "../universe/Universe";
import "./providers.css";

const API_BASE = "" || "";

// ═══════════════════════════════════════════════════════════════════
// Provider Manager — SuperAdmin Settings → AI Providers
// ═══════════════════════════════════════════════════════════════════

function useKernelToken() {
  const [token, setToken] = useState(() => localStorage.getItem("forge:kernel_token") || "");
  const save = (t) => {
    localStorage.setItem("forge:kernel_token", t);
    setToken(t);
  };
  const reset = () => {
    localStorage.removeItem("forge:kernel_token");
    setToken("");
  };
  return [token, save, reset];
}

async function apiGet(path, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`GET ${path} → ${r.status}`);
  return r.json();
}
async function apiJson(path, method, body, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    // Preserva sia il messaggio dettagliato sia lo status HTTP, così i
    // chiamanti possono decidere il fallback basandosi sul codice.
    const err = new Error(data.detail || `${method} ${path} → ${r.status}`);
    err.status = r.status;
    err.detail = data.detail;
    throw err;
  }
  return data;
}

async function apiDelete(path, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`DELETE ${path} → ${r.status}`);
  return r.json().catch(() => ({}));
}

// ── Vault helpers ────────────────────────────────────────────────
async function upsertVaultSecret(name, value, provider, token) {
  // Try create; if 409 (already exists), find & patch
  try {
    return await apiJson("/api/kernel/vault/secrets", "POST", {
      workspace_id: "andrea-forge",
      name, value, kind: "api_key", provider, tags: ["forge-providers"],
    }, token);
  } catch (e) {
    // Match sul codice HTTP (non sul messaggio, che può variare).
    if (e.status !== 409) throw e;
    const list = await apiGet(`/api/kernel/vault/secrets?workspace_id=andrea-forge`, token);
    const existing = (list.secrets || []).find((s) => s.name === name);
    if (!existing) throw e;
    return await apiJson(`/api/kernel/vault/secrets/${existing.id}`, "PATCH", { value }, token);
  }
}

async function deleteVaultSecret(name, token) {
  // Cerca il secret e lo cancella. No-op se non trovato.
  const list = await apiGet(`/api/kernel/vault/secrets?workspace_id=andrea-forge`, token);
  const existing = (list.secrets || []).find((s) => s.name === name);
  if (!existing) return { ok: true, skipped: true };
  return await apiDelete(`/api/kernel/vault/secrets/${existing.id}`, token);
}

// ═══════════════════════════════════════════════════════════════════
// Componenti UI
// ═══════════════════════════════════════════════════════════════════

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`fp-toast ${toast.kind}`} data-testid="fp-toast">
      {toast.msg}
    </div>
  );
}

function TokenGate({ onSave }) {
  const [val, setVal] = useState("");
  return (
    <div className="fp-token-gate" data-testid="fp-token-gate">
      <h2>🔐 FORGE Kernel Token</h2>
      <p>
        Inserisci il token del kernel (variabile <code>FORGE_KERNEL_TOKEN</code> del backend).
        Lo salvo solo in questo browser (localStorage) — nessun invio a server esterni.
      </p>
      <p className="fp-help small">
        <b>Dove trovarlo:</b> sulla VPS in <code>/root/forge-kernel-token.txt</code> · oppure
        via <code>grep FORGE_KERNEL_TOKEN /app/backend/.env</code>.
      </p>
      <div className="row">
        <input
          type="password"
          placeholder="forge-founder-…"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          data-testid="fp-token-input"
        />
        <button
          onClick={() => val && onSave(val)}
          disabled={!val}
          data-testid="fp-token-save"
        >
          Salva token
        </button>
      </div>
    </div>
  );
}

function ProviderRow({ provider, onEdit, onToggle, onTest, testing }) {
  const enabled = provider.enabled !== false;
  return (
    <div className={`fp-row ${enabled ? "" : "disabled"}`} data-testid={`fp-row-${provider.name}`}>
      <div className="fp-row-status">
        <span
          className={`fp-dot ${provider.has_key ? "ok" : "off"}`}
          title={provider.has_key ? "Chiave configurata" : "Chiave mancante"}
        />
      </div>
      <div className="fp-row-info">
        <div className="fp-row-name">
          {provider.label}
          <span className="fp-badge" data-kind={provider.kind}>{provider.kind}</span>
        </div>
        <div className="fp-row-meta">
          <code>{provider.name}</code> · priorità <b>{provider.priority ?? "—"}</b> · modello: <b>{provider.default_model || "—"}</b>
          {provider.base_url ? <> · base <code>{provider.base_url}</code></> : null}
        </div>
      </div>
      <div className="fp-row-actions">
        <button
          className="fp-btn ghost"
          onClick={() => onTest(provider.name)}
          disabled={testing === provider.name}
          data-testid={`fp-test-${provider.name}`}
        >
          {testing === provider.name ? "Test…" : "Test"}
        </button>
        <button
          className={`fp-btn toggle ${enabled ? "on" : "off"}`}
          onClick={() => onToggle(provider.name, !enabled)}
          data-testid={`fp-toggle-${provider.name}`}
          title={enabled ? "Disabilita" : "Abilita"}
        >
          {enabled ? "ON" : "OFF"}
        </button>
        <button
          className="fp-btn primary"
          onClick={() => onEdit(provider)}
          data-testid={`fp-edit-${provider.name}`}
        >
          Configura
        </button>
      </div>
    </div>
  );
}

function EditDrawer({ provider, token, onClose, onSaved, showToast }) {
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(provider.base_url || "");
  const [defaultModel, setDefaultModel] = useState(provider.default_model || "");
  const [priority, setPriority] = useState(provider.priority ?? 100);
  const [enabled, setEnabled] = useState(provider.enabled !== false);
  const [availableModels, setAvailableModels] = useState((provider.available_models || []).join("\n"));
  const [costLimit, setCostLimit] = useState(provider.cost_limit_usd_daily ?? "");
  const [tokenLimit, setTokenLimit] = useState(provider.token_limit_daily ?? "");
  const [notes, setNotes] = useState(provider.notes || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [modelsSource, setModelsSource] = useState(null); // "live" | "static" | null
  const [chipFilter, setChipFilter] = useState("");
  const chipModels = useMemo(
    () => availableModels.split("\n").map((s) => s.trim()).filter(Boolean),
    [availableModels]
  );
  const visibleChips = useMemo(() => {
    const f = chipFilter.trim().toLowerCase();
    return f ? chipModels.filter((m) => m.toLowerCase().includes(f)) : chipModels;
  }, [chipModels, chipFilter]);

  const save = async () => {
    setSaving(true);
    try {
      if (apiKey && provider.vault_key_name) {
        await upsertVaultSecret(provider.vault_key_name, apiKey, provider.name, token);
      }
      await apiJson(`/api/kernel/providers/config/${provider.name}`, "PATCH", {
        enabled,
        priority: Number(priority),
        base_url: baseUrl || null,
        default_model: defaultModel || null,
        available_models: availableModels
          .split("\n").map(s => s.trim()).filter(Boolean),
        cost_limit_usd_daily: costLimit === "" ? null : Number(costLimit),
        token_limit_daily: tokenLimit === "" ? null : Number(tokenLimit),
        notes,
      }, token);
      showToast({ kind: "ok", msg: `${provider.label} salvato ✓` });
      onSaved();
    } catch (e) {
      showToast({ kind: "error", msg: `Salvataggio fallito: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    if (!provider.vault_key_name) {
      showToast({ kind: "error", msg: "Questo provider non gestisce chiavi dal Vault." });
      return;
    }
    if (!window.confirm(`Rimuovere la chiave API di ${provider.label}?\nIl provider tornerà "non configurato" finché non ne inserirai una nuova.`)) return;
    setDeleting(true);
    try {
      await deleteVaultSecret(provider.vault_key_name, token);
      showToast({ kind: "ok", msg: `Chiave ${provider.label} rimossa ✓` });
      onSaved();
    } catch (e) {
      showToast({ kind: "error", msg: `Rimozione fallita: ${e.message}` });
    } finally {
      setDeleting(false);
    }
  };

  const testNow = async () => {
    setTesting(true);
    try {
      const r = await apiJson(`/api/kernel/providers/config/${provider.name}/test`, "POST", {
        api_key: apiKey || undefined,
        base_url: baseUrl || undefined,
        model: defaultModel || undefined,
      }, token);
      if (r.ok) {
        showToast({ kind: "ok", msg: `Ping OK · ${r.model} · ${r.duration_ms}ms` });
      } else {
        showToast({ kind: "error", msg: `Test fallito: ${r.error}` });
      }
    } catch (e) {
      showToast({ kind: "error", msg: `Errore: ${e.message}` });
    } finally {
      setTesting(false);
    }
  };

  const fetchModelsLive = async () => {
    setFetchingModels(true);
    try {
      const r = await apiJson(`/api/kernel/providers/config/${provider.name}/models`, "POST", {
        api_key: apiKey || undefined,
        base_url: baseUrl || undefined,
      }, token);
      setAvailableModels((r.models || []).join("\n"));
      setModelsSource(r.source || (r.ok ? "live" : "static"));
      if (r.ok) {
        showToast({ kind: "ok", msg: `${r.models.length} modelli aggiornati da internet ✓` });
      } else {
        showToast({ kind: "error", msg: `Fetch live fallito (${r.error}) · uso lista statica` });
      }
    } catch (e) {
      showToast({ kind: "error", msg: `Errore: ${e.message}` });
    } finally {
      setFetchingModels(false);
    }
  };

  return (
    <div className="fp-drawer-overlay" onClick={onClose} data-testid="fp-drawer">
      <div className="fp-drawer" onClick={(e) => e.stopPropagation()}>
        <header>
          <h3>{provider.label}</h3>
          <button onClick={onClose} data-testid="fp-drawer-close">✕</button>
        </header>
        <div className="fp-drawer-body">
          <label>API Key {provider.has_key ? <em>· chiave già presente, lascia vuoto per mantenerla</em> : ""}</label>
          <input
            type="password"
            placeholder={provider.has_key ? "•••••••••••••••••" : "sk-…"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            data-testid="fp-apikey"
          />
          {provider.docs_url && (
            <p className="fp-help">
              Ottieni la chiave: <a href={provider.docs_url} target="_blank" rel="noreferrer">{provider.docs_url}</a>
            </p>
          )}

          {provider.supports_base_url && (
            <>
              <label>Base URL {provider.name === "custom" ? "· obbligatorio per OpenAI-compatible" : "· opzionale"}</label>
              <input
                type="text"
                placeholder="https://api.example.com/v1"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                data-testid="fp-baseurl"
              />
            </>
          )}

          <label>Modello di default</label>
          <input
            type="text"
            placeholder={provider.example_models?.[0] || ""}
            value={defaultModel}
            onChange={(e) => setDefaultModel(e.target.value)}
            data-testid="fp-defaultmodel"
          />

          <div className="fp-models-head">
            <label>
              Modelli disponibili (uno per riga)
              {modelsSource && (
                <span className={`fp-models-source ${modelsSource}`}>
                  {modelsSource === "live" ? "· aggiornati da internet ✓" : "· lista statica"}
                </span>
              )}
            </label>
            {provider.name !== "custom" && (
              <button
                type="button"
                className="fp-btn ghost small"
                onClick={fetchModelsLive}
                disabled={fetchingModels}
                data-testid="fp-fetch-models"
                title="Interroga l'API reale del provider per la lista modelli aggiornata"
              >
                {fetchingModels ? "Aggiorno…" : "🔄 Aggiorna modelli"}
              </button>
            )}
          </div>
          <textarea
            rows={4}
            value={availableModels}
            onChange={(e) => setAvailableModels(e.target.value)}
            placeholder={(provider.example_models || []).join("\n")}
            data-testid="fp-models"
          />
          {chipModels.length > 0 && (
            <>
              {chipModels.length > 12 && (
                <input
                  type="text"
                  className="fp-model-filter"
                  placeholder={`Filtra tra ${chipModels.length} modelli…`}
                  value={chipFilter}
                  onChange={(e) => setChipFilter(e.target.value)}
                  data-testid="fp-model-filter"
                />
              )}
              <div className="fp-model-chips" data-testid="fp-model-chips">
                {visibleChips.map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={`fp-model-chip ${defaultModel === m ? "active" : ""}`}
                    onClick={() => setDefaultModel(m)}
                    data-testid={`fp-model-chip-${m}`}
                    title="Imposta come modello di default"
                  >
                    {m}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="row-2">
            <div>
              <label>Priorità (0 = più alta)</label>
              <input
                type="number" min={0} max={999}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                data-testid="fp-priority"
              />
            </div>
            <div>
              <label>Abilitato</label>
              <button
                type="button"
                className={`fp-btn toggle ${enabled ? "on" : "off"}`}
                onClick={() => setEnabled(!enabled)}
                data-testid="fp-enabled"
              >
                {enabled ? "ON" : "OFF"}
              </button>
            </div>
          </div>

          <div className="row-2">
            <div>
              <label>Cost limit ($ / giorno)</label>
              <input
                type="number" min={0} step={0.01}
                value={costLimit}
                onChange={(e) => setCostLimit(e.target.value)}
                placeholder="es. 5.00"
              />
            </div>
            <div>
              <label>Token limit (giorno)</label>
              <input
                type="number" min={0}
                value={tokenLimit}
                onChange={(e) => setTokenLimit(e.target.value)}
                placeholder="es. 200000"
              />
            </div>
          </div>

          <label>Note</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Contesto, uso previsto, ecc."
          />
        </div>
        <footer>
          <button className="fp-btn ghost" onClick={testNow} disabled={testing} data-testid="fp-drawer-test">
            {testing ? "Test…" : "Test connessione"}
          </button>
          {provider.has_key && provider.vault_key_name && (
            <button
              className="fp-btn danger"
              onClick={removeKey}
              disabled={deleting}
              data-testid="fp-drawer-remove-key"
              title="Rimuovi la chiave API salvata"
            >
              {deleting ? "Rimuovo…" : "🗑 Rimuovi chiave"}
            </button>
          )}
          <div className="spacer" />
          <button className="fp-btn ghost" onClick={onClose}>Annulla</button>
          <button className="fp-btn primary" onClick={save} disabled={saving} data-testid="fp-drawer-save">
            {saving ? "Salvo…" : "Salva"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function ProvidersPage() {
  const [token, saveToken, resetToken] = useKernelToken();
  const [providers, setProviders] = useState([]);
  const [taskRouting, setTaskRouting] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [toast, setToast] = useState(null);

  // Nasconde il FAB globale di Aria mentre il drawer è aperto: a schermi
  // grandi il FAB (z-index altissimo, fisso in basso a destra) copre il
  // bottone "Salva" del drawer e ne blocca il click reale col mouse.
  useEffect(() => {
    document.body.classList.toggle("fp-drawer-open", !!editing);
    return () => document.body.classList.remove("fp-drawer-open");
  }, [editing]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet("/api/kernel/providers/config", token);
      setProviders(r.providers || []);
      setTaskRouting(r.task_routing || {});
    } catch (e) {
      setToast({ kind: "error", msg: `Caricamento fallito: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (name, enabled) => {
    try {
      await apiJson(`/api/kernel/providers/config/${name}`, "PATCH", { enabled }, token);
      setToast({ kind: "ok", msg: `${name}: ${enabled ? "abilitato" : "disabilitato"}` });
      load();
    } catch (e) {
      setToast({ kind: "error", msg: `Errore: ${e.message}` });
    }
  };

  const runTest = async (name) => {
    setTesting(name);
    try {
      const r = await apiJson(`/api/kernel/providers/config/${name}/test`, "POST", {}, token);
      if (r.ok) {
        setToast({ kind: "ok", msg: `${name} OK · ${r.model} · ${r.duration_ms}ms` });
      } else {
        setToast({ kind: "error", msg: `${name}: ${r.error}` });
      }
    } catch (e) {
      setToast({ kind: "error", msg: `Errore: ${e.message}` });
    } finally {
      setTesting(null);
    }
  };

  const configured = providers.filter(p => p.has_key).length;
  const enabled = providers.filter(p => p.enabled !== false).length;

  return (
    <div className="fp-page">
      <ForgeNavBar current="/providers" />

      <div className="fp-container">
        <header className="fp-header">
          <div>
            <h1>🔐 AI Providers</h1>
            <p>Forge è provider-agnostic. Aggiungi le tue chiavi, decidi priorità e limiti. Aria orchestra automaticamente.</p>
          </div>
          <div className="fp-stats">
            <div><b>{configured}</b><span>configurati</span></div>
            <div><b>{enabled}</b><span>abilitati</span></div>
            <div><b>{providers.length}</b><span>totali</span></div>
            {token && (
              <button
                className="fp-btn ghost small"
                onClick={() => {
                  if (window.confirm("Rimuovere il Kernel Token da questo browser?\nDovrai reinserirlo per operare.")) {
                    resetToken();
                    setToast({ kind: "ok", msg: "Kernel Token rimosso ✓" });
                  }
                }}
                data-testid="fp-token-reset"
                title="Rimuovi il token salvato in questo browser"
                style={{ marginLeft: 8 }}
              >
                🔓 Cambia token
              </button>
            )}
          </div>
        </header>

        {!token && <TokenGate onSave={saveToken} />}

        {token && (
          <>
            <section className="fp-section">
              <div className="fp-section-title">
                <span>Provider</span>
                <button className="fp-btn ghost small" onClick={load} data-testid="fp-refresh">
                  ↻ Aggiorna
                </button>
              </div>
              {loading ? (
                <div className="fp-loading">Carico…</div>
              ) : (
                <div className="fp-list">
                  {providers.map((p) => (
                    <ProviderRow
                      key={p.name}
                      provider={p}
                      testing={testing}
                      onEdit={setEditing}
                      onToggle={toggle}
                      onTest={runTest}
                    />
                  ))}
                </div>
              )}
            </section>

            <section className="fp-section">
              <div className="fp-section-title"><span>Task-based routing (Fase B)</span></div>
              <div className="fp-routing">
                {Object.entries(taskRouting).map(([task, prefs]) => (
                  <div key={task} className="fp-routing-row">
                    <b>{task}</b>
                    <span>→</span>
                    <div className="chips">
                      {prefs.map((p, i) => <span key={p} className="chip" data-rank={i}>{p}</span>)}
                    </div>
                  </div>
                ))}
              </div>
              <p className="fp-help small">
                Aria sceglie automaticamente il provider migliore per il tipo di task (reasoning, coding, contesto lungo, ecc.).
                Personalizzazione delle preferenze in arrivo nella Fase B.
              </p>
            </section>
          </>
        )}
      </div>

      {editing && (
        <EditDrawer
          provider={editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          showToast={setToast}
        />
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
